import React, { useEffect, useState } from "react";
import AlertSuccess from "../componets/AlerSuccess";
import { useWindowSize } from "../componets/useWindowSize";
import {
  getButtonCollapsedMemory,
  getLimitePresenca,
  getMenuCollapsedMemory,
  getVersionApp,
  setButtonCollapsedmemory,
  setMenuCollapsedmemory,
} from "../data/memory";
import { getDatabase, ref, onValue, remove } from "firebase/database";
import { autoResetJogosMensal } from "../data/autoClear";
import { resetList } from "../data/resetList";
import AppShell from "../componets/AppShell";

interface HistoricoEntry {
  data: string;
  displayName: string;
  tipo: "inscricao" | "cancelamento" | "limpeza" | "delecao";
  totalJogos?: number | null;
}

interface AttendanceData {
  uid: string;
  order: number;
  name: string;
  time: string;
  rawTime: number;
  numberOfGames: number;
  isBirthday: boolean;
  isTuesday: boolean;
}

const Historico: React.FC = () => {
  const { width } = useWindowSize();
  const [historico, setHistorico] = useState<HistoricoEntry[]>([]);
  const [attendanceList, setAttendanceList] = useState<AttendanceData[]>([]);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(
    getMenuCollapsedMemory()
  );
  const [isButtonCollapsed, setIsButtonCollapsed] = useState(
    getButtonCollapsedMemory()
  );
  const [activeTab, setActiveTab] = useState<"historico" | "lista">(
    "historico"
  );

  const [successAlert, setSuccessAlert] = useState({
    open: false,
    message: "",
  });

  useEffect(() => {
    resetList();
    autoResetJogosMensal();
    fetchAttendanceList();
  }, []);

  useEffect(() => {
    if (width < 1000) {
      setIsMenuCollapsed(true);
      setMenuCollapsedmemory(true);
    } else if (!getButtonCollapsedMemory()) {
      setIsMenuCollapsed(false);
      setMenuCollapsedmemory(false);
    }
  }, [width]);

  const handleMenuToggle = () => {
    const newButtonCollapsed = !isButtonCollapsed;
    const newMenuCollapsed = !isMenuCollapsed;
    setIsButtonCollapsed(newButtonCollapsed);
    setButtonCollapsedmemory(newButtonCollapsed);
    setIsMenuCollapsed(newMenuCollapsed);
    setMenuCollapsedmemory(newMenuCollapsed);
  };

  useEffect(() => {
    const db = getDatabase();
    const historicoRef = ref(db, "historico");

    const unsubscribe = onValue(historicoRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const entries = Object.entries(data).map(([key, value]: any) => ({
          key,
          data: value.data,
          displayName: value.displayName || "Usuário",
          tipo: value.tipo,
          totalJogos: value.totalJogos ?? null,
        }));

        const sorted = entries.sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

        if (sorted.length > 100) {
          const excessEntries = sorted.slice(100);
          const updates = excessEntries.map((entry) =>
            remove(ref(db, `historico/${entry.key}`))
          );
          await Promise.all(updates);
        }

        setHistorico(sorted.slice(0, 100).map(({ key, ...rest }) => rest));
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchAttendanceList = () => {
    const db = getDatabase();
    const presencaRef = ref(db, "presencahistorico");

    onValue(presencaRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setAttendanceList([]);
        return;
      }

      const entries: AttendanceData[] = Object.entries(data).map(
        ([uid, entry]: [string, any]) => {
          const inscricaoDate = new Date(entry.timestamp);
          return {
            uid,
            order: 0,
            name: entry.displayName || "Usuário",
            time: formatHoraComMilissegundos(entry.timestamp),
            rawTime: inscricaoDate.getTime(),
            numberOfGames: entry.totalJogos || 0,
            isBirthday: entry.aniversario
              ? new Date(entry.aniversario).getMonth() + 1 ===
                new Date().getMonth()
              : false,
            isTuesday: entry.isTuesday || false,
          };
        }
      );

      const segundaFeira = entries.filter((e) => !e.isTuesday);
      const tercaFeira = entries.filter((e) => e.isTuesday);

      const ordenados = [
        ...segundaFeira.sort((a, b) => {
          if (a.numberOfGames !== b.numberOfGames) {
            return a.numberOfGames - b.numberOfGames;
          }
          return a.rawTime - b.rawTime;
        }),
        ...tercaFeira.sort((a, b) => a.rawTime - b.rawTime),
      ].map((entry, index) => ({
        ...entry,
        order: index + 1,
      }));

      setAttendanceList(ordenados);
    });
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatHoraComMilissegundos = (timestamp: string | number): string => {
    const date = new Date(timestamp);
    const horas = date.getHours().toString().padStart(2, "0");
    const minutos = date.getMinutes().toString().padStart(2, "0");
    const segundos = date.getSeconds().toString().padStart(2, "0");
    const milissegundos = date.getMilliseconds().toString().padStart(3, "0");

    return `${horas}:${minutos}:${segundos}.${milissegundos}`;
  };

  const formatAcao = (tipo: HistoricoEntry["tipo"]) => {
    switch (tipo) {
      case "inscricao":
        return "Inscrição";
      case "cancelamento":
        return "Cancelamento";
      case "limpeza":
        return "Limpeza da lista";
      case "delecao":
        return "Deletou a conta";
    }
  };

  return (
    <>
      <AlertSuccess
        isOpen={successAlert.open}
        message={successAlert.message}
        onClose={() => setSuccessAlert({ open: false, message: "" })}
      />
      <AppShell
        isMenuCollapsed={isMenuCollapsed}
        onToggleMenu={handleMenuToggle}
      >
        <section className="panel-glass flex min-h-[calc(100dvh-7rem)] w-full flex-col rounded-[2rem] p-4 text-white sm:p-8">
          <div className="mb-6 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold sm:text-2xl">Histórico</h2>
            <span className="rounded-full border border-pink-300/20 bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-100">
              Ações e listas
            </span>
          </div>

          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => setActiveTab("historico")}
              className={`rounded-2xl px-4 py-2 font-semibold transition ${
                activeTab === "historico"
                  ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-[0_18px_40px_rgba(244,114,182,0.22)]"
                  : "border border-white/10 bg-white/[0.06] text-zinc-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              Histórico de Ações
            </button>
            <button
              onClick={() => setActiveTab("lista")}
              className={`rounded-2xl px-4 py-2 font-semibold transition ${
                activeTab === "lista"
                  ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-[0_18px_40px_rgba(244,114,182,0.22)]"
                  : "border border-white/10 bg-white/[0.06] text-zinc-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              Lista passada do play
            </button>
          </div>

          <div className="panel-soft mb-6 flex-1 w-full rounded-[1.5rem] p-4 sm:p-6">
            {activeTab === "historico" && (
              <div className="custom-scrollbar w-full overflow-x-auto rounded-[1.25rem]">
                <table className="min-w-full table-auto text-xs sm:text-sm md:text-base">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-zinc-300">
                      <th className="px-2 py-2 sm:px-4 sm:py-3">Data/Hora</th>
                      <th className="px-2 py-2 sm:px-4 sm:py-3">Nome</th>
                      <th className="px-2 py-2 sm:px-4 sm:py-3">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historico.map((entry, index) => (
                      <tr
                        key={index}
                        className="border-b border-white/8 text-zinc-100 transition hover:bg-white/[0.06]"
                      >
                        <td className="whitespace-nowrap px-2 py-2 sm:px-4 sm:py-3">
                          <span className="block sm:hidden">
                            {(() => {
                              const [date, time] = formatTimestamp(
                                entry.data
                              ).split(" ");
                              return `${date.slice(0, 5)} ${time.slice(0, 5)}`;
                            })()}
                          </span>
                          <span className="hidden sm:block">
                            {formatTimestamp(entry.data)}
                          </span>
                        </td>
                        <td className="max-w-[120px] truncate px-2 py-2 sm:max-w-none sm:px-4 sm:py-3">
                          {entry.displayName}
                        </td>
                        <td className="px-2 py-2 sm:px-4 sm:py-3">
                          {formatAcao(entry.tipo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "lista" && (
              <div className="custom-scrollbar w-full overflow-x-auto rounded-[1.25rem]">
                <table className="min-w-full table-fixed text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-zinc-300">
                      <th className="w-10 px-1 py-2 sm:px-4">Ordem</th>
                      <th className="px-1 py-2 sm:px-4">Nome</th>
                      <th className="w-20 px-1 py-2 sm:px-4">Horário</th>
                      <th className="w-16 px-1 py-2 text-right sm:px-4">
                        <span className="block sm:hidden">Nº Jogos</span>
                        <span className="hidden sm:block">Nº de Jogos</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceList.map((attendance, index) => (
                      <tr
                        key={attendance.uid}
                        className={`border-b border-white/8 transition hover:bg-white/[0.06] ${
                          index >= getLimitePresenca()
                            ? "text-pink-300"
                            : "text-zinc-100"
                        }`}
                      >
                        <td className="w-10 px-1 py-2 sm:px-4">
                          {attendance.order}
                        </td>
                        <td className="px-1 py-2 sm:px-4">
                          <div className="max-w-[150px] truncate sm:max-w-none">
                            {attendance.name}
                          </div>
                        </td>
                        <td className="w-20 truncate px-1 py-2 sm:px-4">
                          {attendance.time}
                        </td>
                        <td className="w-16 px-1 py-2 text-right sm:px-4">
                          {attendance.numberOfGames}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <div className="w-full py-2 text-center">
          <span className="text-xs text-zinc-500 sm:text-sm">
            Developed by Samuel Stork
          </span>{" "}
          <span className="text-xs text-zinc-600 sm:text-sm">
            {getVersionApp()}
          </span>
        </div>
      </AppShell>
    </>
  );
};

export default Historico;
